import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { useActiveScrollContainer } from "@/contexts/ScrollContainerContext";
import { shouldUsePullToRefresh } from "@/platform";

interface WebPullToRefreshProps {
  onRefresh: () => Promise<void> | void;
  children: ReactNode;
  className?: string;
  active: boolean;
}

const PULL_THRESHOLD = 80;
const RESISTANCE = 2.5;
const INDICATOR_SIZE = 44;
const INDICATOR_CONTENT_GAP = 8;
const INDICATOR_TOP_GAP = 8;

/**
 * Pull-to-refresh for Android deploys. Everywhere else this just renders its children.
 */
export const WebPullToRefresh = ({
  onRefresh,
  children,
  className,
  active,
}: WebPullToRefreshProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollContainer = useActiveScrollContainer();
  const pullDistance = useMotionValue(0);
  const spinnerOpacity = useTransform(pullDistance, value => (isRefreshing ? 1 : Math.min(value / PULL_THRESHOLD, 1)));
  const spinnerScale = useTransform(pullDistance, value => (isRefreshing ? 1 : Math.min(value / PULL_THRESHOLD, 1)));
  const spinnerTop = useTransform(
    pullDistance,
    value => `calc(var(--sat, 0px) + ${Math.max(value - INDICATOR_SIZE - INDICATOR_CONTENT_GAP, INDICATOR_TOP_GAP)}px)`,
  );
  const spinnerRotate = useTransform(pullDistance, value => (value / PULL_THRESHOLD) * 360);

  const touchStartY = useRef(0);
  const touchStartX = useRef(0);
  const isPulling = useRef(false);
  const gestureDecided = useRef(false);
  const isHorizontalGesture = useRef(false);
  const pullDistanceRef = useRef(0);
  const isRefreshingRef = useRef(false);
  const pullFrameRef = useRef<number | null>(null);
  const snapAnimationRef = useRef<ReturnType<typeof animate> | null>(null);

  const pullToRefresh = shouldUsePullToRefresh();
  const gestureActive = active && pullToRefresh;

  useEffect(() => {
    isRefreshingRef.current = isRefreshing;
  }, [isRefreshing]);

  const stopSnapAnimation = useCallback(() => {
    if (snapAnimationRef.current) {
      snapAnimationRef.current.stop();
      snapAnimationRef.current = null;
    }
  }, []);

  const setPullDistanceNow = useCallback(
    (nextDistance: number) => {
      pullDistanceRef.current = Math.max(0, nextDistance);
      pullDistance.set(pullDistanceRef.current);
    },
    [pullDistance],
  );

  const schedulePullDistance = useCallback(
    (nextDistance: number) => {
      pullDistanceRef.current = Math.max(0, nextDistance);
      if (pullFrameRef.current !== null) return;

      pullFrameRef.current = window.requestAnimationFrame(() => {
        pullFrameRef.current = null;
        pullDistance.set(pullDistanceRef.current);
      });
    },
    [pullDistance],
  );

  const snapPullDistance = useCallback(
    (nextDistance: number, onComplete?: () => void) => {
      stopSnapAnimation();
      pullDistanceRef.current = Math.max(0, nextDistance);
      snapAnimationRef.current = animate(pullDistance, pullDistanceRef.current, {
        duration: 0.2,
        ease: "easeOut",
        onComplete,
      });
    },
    [pullDistance, stopSnapAnimation],
  );

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (isRefreshingRef.current || e.touches.length !== 1) return;

      stopSnapAnimation();
      gestureDecided.current = false;
      isHorizontalGesture.current = false;
      isPulling.current = false;

      touchStartY.current = e.touches[0].clientY;
      touchStartX.current = e.touches[0].clientX;
    },
    [stopSnapAnimation],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (isRefreshingRef.current || e.touches.length !== 1) return;

      const currentY = e.touches[0].clientY;
      const currentX = e.touches[0].clientX;
      const deltaY = currentY - touchStartY.current;
      const deltaX = currentX - touchStartX.current;

      if (!gestureDecided.current) {
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);

        if (absX > 10 || absY > 10) {
          gestureDecided.current = true;
          if (absX > absY) {
            isHorizontalGesture.current = true;
            return;
          }
        } else {
          return;
        }
      }

      if (isHorizontalGesture.current) {
        return;
      }

      const scrollTop = scrollContainer ? scrollContainer.scrollTop : window.scrollY || document.documentElement.scrollTop;
      if (scrollTop > 0) {
        if (isPulling.current) {
          isPulling.current = false;
          setPullDistanceNow(0);
        }
        return;
      }

      if (deltaY > 0) {
        isPulling.current = true;
        const distance = deltaY / RESISTANCE;
        schedulePullDistance(Math.min(distance, PULL_THRESHOLD * 1.5));
        e.preventDefault();
      } else if (isPulling.current) {
        isPulling.current = false;
        setPullDistanceNow(0);
      }
    },
    [schedulePullDistance, scrollContainer, setPullDistanceNow],
  );

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling.current || isRefreshingRef.current) {
      snapPullDistance(0);
      return;
    }

    const shouldTrigger = pullDistanceRef.current >= PULL_THRESHOLD;
    isPulling.current = false;

    if (shouldTrigger) {
      setIsRefreshing(true);
      snapPullDistance(PULL_THRESHOLD);

      try {
        await onRefresh();
      } catch (error) {
        console.error("Refresh failed:", error);
      } finally {
        setIsRefreshing(false);
        snapPullDistance(0);
      }
    } else {
      snapPullDistance(0);
    }
  }, [onRefresh, snapPullDistance]);

  useEffect(() => {
    if (!gestureActive) return;

    const content = contentRef.current;
    if (!content) return;

    content.addEventListener("touchstart", handleTouchStart, { passive: true, capture: true });
    content.addEventListener("touchmove", handleTouchMove, { passive: false, capture: true });
    content.addEventListener("touchend", handleTouchEnd, { passive: true, capture: true });
    content.addEventListener("touchcancel", handleTouchEnd, { passive: true, capture: true });

    return () => {
      content.removeEventListener("touchstart", handleTouchStart, { capture: true });
      content.removeEventListener("touchmove", handleTouchMove, { capture: true });
      content.removeEventListener("touchend", handleTouchEnd, { capture: true });
      content.removeEventListener("touchcancel", handleTouchEnd, { capture: true });
    };
  }, [gestureActive, handleTouchStart, handleTouchMove, handleTouchEnd]);

  useEffect(() => {
    if (gestureActive) return;

    isPulling.current = false;
    gestureDecided.current = false;
    setPullDistanceNow(0);
  }, [gestureActive, setPullDistanceNow]);

  useEffect(() => {
    return () => {
      if (pullFrameRef.current !== null) {
        window.cancelAnimationFrame(pullFrameRef.current);
      }
      stopSnapAnimation();
    };
  }, [stopSnapAnimation]);

  if (!pullToRefresh) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div ref={contentRef} className={`relative ${className || ""}`}>
      <motion.div
        className="absolute left-1/2 z-50 pointer-events-none"
        style={{
          top: spinnerTop,
          opacity: spinnerOpacity,
          x: "-50%",
          scale: spinnerScale,
        }}
      >
        <div className="flex h-11 w-11 items-center justify-center bg-background rounded-full border border-border shadow-sm">
          <motion.div style={{ rotate: spinnerRotate }}>
            <Loader2
              className="text-primary"
              size={20}
              style={{
                animation: isRefreshing ? "spin 0.8s linear infinite" : "none",
              }}
            />
          </motion.div>
        </div>
      </motion.div>

      <motion.div className="page-refresh-content" style={{ y: pullDistance }}>
        {children}
      </motion.div>
    </div>
  );
};
